/**
 * OMR Card Layout Constants
 * All measurements in millimeters (will be converted to pixels based on DPI)
 *
 * Standard A4 paper: 210mm × 297mm
 * Print DPI: 96 (screen) or 300 (print) - CSS handles conversion with mm units
 */

// Page dimensions (A4)
export const PAGE_WIDTH_MM = 210;
export const PAGE_HEIGHT_MM = 297;
export const PAGE_MARGIN_MM = 10;

// Usable area
export const USABLE_WIDTH_MM = PAGE_WIDTH_MM - (PAGE_MARGIN_MM * 2); // 190mm
export const USABLE_HEIGHT_MM = PAGE_HEIGHT_MM - (PAGE_MARGIN_MM * 2); // 277mm

// Fiducials (registration marks) - 4 corners
export const FIDUCIAL_SIZE_MM = 15;
export const FIDUCIAL_POSITION = {
  topLeft: { x: PAGE_MARGIN_MM, y: PAGE_MARGIN_MM },
  topRight: { x: PAGE_WIDTH_MM - PAGE_MARGIN_MM - FIDUCIAL_SIZE_MM, y: PAGE_MARGIN_MM },
  bottomLeft: { x: PAGE_MARGIN_MM, y: PAGE_HEIGHT_MM - PAGE_MARGIN_MM - FIDUCIAL_SIZE_MM },
  bottomRight: { x: PAGE_WIDTH_MM - PAGE_MARGIN_MM - FIDUCIAL_SIZE_MM, y: PAGE_HEIGHT_MM - PAGE_MARGIN_MM - FIDUCIAL_SIZE_MM },
};

// Student ID Area (CPF - 11 digits)
export const STUDENT_ID_START_Y_MM = 35; // 35mm from top (after header and labels)
export const STUDENT_ID_END_Y_MM = 99;   // 99mm from top (64mm height for 11×10 grid)
export const STUDENT_ID_DIGITS = 11;     // 11 digits for CPF
export const STUDENT_ID_OPTIONS = 10;    // 0-9 (each digit has 10 options)

// Bubble specifications
// Updated from Canva design: smaller bubbles for better OMR processing
export const BUBBLE_DIAMETER_MM = 4.5;   // Question bubbles (A-E)
export const BUBBLE_SPACING_MM = 2.2;    // Space between bubbles
export const BUBBLE_STROKE_WIDTH_PT = 0.5;
export const BUBBLE_LETTER_FONT_SIZE_PT = 7; // Smaller font for centered inside bubble

// ID Grid (CPF bubbles - smaller than question bubbles)
export const ID_BUBBLE_DIAMETER_MM = 3.5;
export const ID_BUBBLE_SPACING_VERTICAL_MM = 2.2;   // Between rows (options 0-9)
export const ID_BUBBLE_SPACING_HORIZONTAL_MM = 2.2; // Between columns (digits)

// Timing marks (sincronização)
export const TIMING_MARK_WIDTH_MM = 3;
export const TIMING_MARK_HEIGHT_MM = 1.5;
export const TIMING_MARK_DISTANCE_FROM_EDGE_MM = 5; // 5mm from left/right edge

// Questions layout
export const QUESTIONS_START_Y_MM = STUDENT_ID_END_Y_MM + 10; // 109mm - After student ID area (with 10mm gap)
export const QUESTIONS_PER_COLUMN = 15;  // Questions split into 2 columns (1-15, 16-30)
export const QUESTION_LINE_HEIGHT_MM = 10; // Height allocated per question (updated for smaller bubbles)
export const QUESTION_NUMBER_FONT_SIZE_PT = 9;
export const ALTERNATIVES = ['A', 'B', 'C', 'D', 'E'];

// Layout spacing
export const QUESTION_COLUMN_SPACING_MM = 10; // Space between left and right columns
export const HEADER_HEIGHT_MM = 25; // Space for school name, exam code, etc.

// SVG/Canvas scaling
// Convert mm to pixels: 1mm = 3.78px (at 96 DPI)
// CSS will handle this automatically with mm units
export const MM_TO_PX = 3.78;
export const PX_TO_MM = 1 / MM_TO_PX;

/**
 * Calculate pixel value from millimeters
 * For use in JavaScript calculations only (CSS uses mm directly)
 */
export const mmToPx = (mm) => mm * MM_TO_PX;
export const pxToMm = (px) => px * PX_TO_MM;

/**
 * Get bubble position within a question's alternative
 */
export const getBubblePosition = (columnIndex) => {
  const totalWidth = (BUBBLE_DIAMETER_MM * ALTERNATIVES.length) +
                     (BUBBLE_SPACING_MM * (ALTERNATIVES.length - 1));
  const startX = (USABLE_WIDTH_MM - totalWidth) / 2;
  return {
    x: startX + (columnIndex * (BUBBLE_DIAMETER_MM + BUBBLE_SPACING_MM)),
    y: 0, // Relative to question line
  };
};
